import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, FileWarning, LoaderCircle, MessageSquareText } from 'lucide-react';
import {
  ContentReport,
  loadReportEvents,
  ReportEvent
} from '../../backend/moderationService';
import { formatCount } from '../../backend/mediaService';
import { useReports } from '../../hooks/useReports';
import { ReportResponseModal } from './ReportResponseModal';

type ReportsData = ReturnType<typeof useReports>;
type ReportSection = 'received' | 'filed' | 'history';

interface ReportsTabProps {
  active: boolean;
  data: ReportsData;
}

function reportDate(value: unknown): string {
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return (value.toDate() as Date).toLocaleString();
  }
  const date = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'Date unavailable';
}

function statusLabel(status: ContentReport['status']): string {
  return ({ open: 'Open', under_review: 'Under review', actioned: 'Actioned', dismissed: 'Dismissed' })[status] || 'Open';
}

function eventLabel(event: ReportEvent, isSelf: boolean): string {
  if (event.type === 'owner_response') {
    const action = ({ will_correct: 'will correct it', corrected: 'corrected it', keep_as_is: 'kept it as is' } as Record<string, string>)[event.action] || event.action;
    return `${isSelf ? 'You' : 'Content owner'} responded: ${action}${event.note ? ` - ${event.note}` : ''}`;
  }
  const labels: Record<string, string> = {
    under_review: 'Admin moved the report under review',
    dismissed: 'Admin dismissed the report',
    warning_sent: 'Admin sent a warning',
    content_removed: 'Admin removed content',
    user_suspended: 'Admin suspended the account'
  };
  return labels[event.action] || 'Admin updated the report';
}

export function ReportsTab({ active, data }: ReportsTabProps) {
  const [section, setSection] = useState<ReportSection>('received');
  const [expandedContent, setExpandedContent] = useState<string | null>(null);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  const [eventsByReport, setEventsByReport] = useState<Record<string, ReportEvent[]>>({});
  const [eventsError, setEventsError] = useState('');
  const [eventsLoading, setEventsLoading] = useState<string | null>(null);
  const [responseReport, setResponseReport] = useState<ContentReport | null>(null);

  useEffect(() => {
    if (!active) return;
    if (section === 'received' && data.received.length > 0) return;
    if (section === 'filed' && data.filed.length > 0) return;
    if (section === 'history' && data.timeline.length > 0) return;
    const kind = section === 'history' ? 'timeline' : section;
    void data.loadReportPage(kind);
  }, [active, data.filed.length, data.loadReportPage, data.received.length, data.timeline.length, section]);

  const groupedReceived = useMemo(() => {
    const groups = new Map<string, ContentReport[]>();
    data.received.forEach(report => {
      const key = report.contentId;
      groups.set(key, [...(groups.get(key) || []), report]);
    });
    return [...groups.entries()];
  }, [data.received]);

  const loadEvents = async (reportId: string, force = false) => {
    if (!force && eventsByReport[reportId]) return;
    setEventsLoading(reportId);
    setEventsError('');
    try {
      const result = await loadReportEvents(reportId);
      setEventsByReport(previous => ({ ...previous, [reportId]: result.events }));
    } catch (error) {
      setEventsError(error instanceof Error ? error.message : 'Could not load report activity.');
    } finally {
      setEventsLoading(null);
    }
  };

  const toggleReport = (reportId: string) => {
    setExpandedReport(previous => previous === reportId ? null : reportId);
    void loadEvents(reportId);
  };

  const currentItems = section === 'received'
    ? data.received
    : section === 'filed'
      ? data.filed
      : data.timeline;
  const loading = section === 'received' ? data.receivedLoading : section === 'filed' ? data.filedLoading : data.timelineLoading;
  const hasMore = section === 'received' ? data.receivedHasMore : section === 'filed' ? data.filedHasMore : data.timelineHasMore;

  return (
    <section className="space-y-4 py-4">
      <div className="flex border-b border-slate-200">
        {([
          ['received', `Received (${formatCount(data.receivedCount)})`],
          ['filed', `Filed by me (${formatCount(data.filedCount)})`],
          ['history', 'Action history']
        ] as const).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setSection(id)} className={`min-h-11 flex-1 border-b-2 px-1 text-[11px] font-bold sm:text-xs ${section === id ? 'border-blue-900 text-blue-950' : 'border-transparent text-slate-500'}`}>{label}</button>
        ))}
      </div>

      {section === 'filed' && <p className="text-sm font-semibold text-slate-600">You have reported {formatCount(data.filedCount)} contents.</p>}
      {data.error && <p role="alert" className="rounded-xl bg-rose-50 p-3 text-xs text-rose-700">{data.error.message}</p>}
      {eventsError && <p role="alert" className="rounded-xl bg-rose-50 p-3 text-xs text-rose-700">{eventsError}</p>}

      {section === 'received' && groupedReceived.map(([contentId, reports]) => {
        const newest = reports[0];
        const isExpanded = expandedContent === contentId;
        return (
          <article key={contentId} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <button type="button" onClick={() => setExpandedContent(isExpanded ? null : contentId)} className="flex w-full items-center gap-3 p-3 text-left">
              {newest.contentThumbUrl
                ? <img src={newest.contentThumbUrl} alt="" className="h-14 w-14 rounded-xl bg-slate-100 object-cover" />
                : <div className="grid h-14 w-14 place-items-center rounded-xl bg-gradient-to-br from-slate-100 to-blue-100"><FileWarning className="h-5 w-5 text-slate-500" /></div>}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold text-slate-900">{newest.contentTitle || 'Reported content'}</span>
                <span className="mt-1 block text-xs text-slate-500">{reports.length} {reports.length === 1 ? 'report' : 'reports'} · {statusLabel(newest.status)}</span>
              </span>
              {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
            </button>
            {isExpanded && <div className="space-y-3 border-t border-slate-100 p-3">
              {reports.map((report, index) => {
                const reportEvents = eventsByReport[report.id] || [];
                const response = reportEvents.find(event => event.type === 'owner_response');
                return <div key={report.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-800">{report.reason}</span>
                    <span className="rounded-full bg-white px-2 py-1 text-[10px] font-semibold text-slate-600">{statusLabel(report.status)}</span>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-600">{report.details || 'No additional details provided.'}</p>
                  <p className="mt-1 text-[10px] text-slate-400">Reporter {index + 1} · {reportDate(report.reportedAt)}</p>
                  {eventsLoading === report.id && <p className="mt-2 text-[10px] text-slate-500">Loading response…</p>}
                  {response && <p className="mt-2 text-[10px] font-medium text-emerald-800">You responded on {reportDate(response.createdAt)}.</p>}
                  {report.status === 'open' && <button type="button" onClick={() => setResponseReport(report)} className="mt-2 rounded-lg bg-blue-950 px-3 py-2 text-[10px] font-bold text-white">{response ? 'Respond again' : 'Respond'}</button>}
                  <button type="button" onClick={() => toggleReport(report.id)} className="ml-2 mt-2 text-[10px] font-semibold text-blue-800">{expandedReport === report.id ? 'Hide activity' : 'View activity'}</button>
                  {expandedReport === report.id && reportEvents.map(event => <p key={event.id} className="mt-2 border-t border-slate-200 pt-2 text-[10px] text-slate-600">{eventLabel(event, true)} · {reportDate(event.createdAt)}</p>)}
                </div>;
              })}
            </div>}
          </article>
        );
      })}

      {section === 'filed' && data.filed.map(report => {
        const events = eventsByReport[report.id] || [];
        const lastAdminEvent = events.find(event => event.type === 'admin_action');
        return <article key={report.id} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-3">
          {report.contentThumbUrl
            ? <img src={report.contentThumbUrl} alt="" className="h-14 w-14 rounded-xl bg-slate-100 object-cover" />
            : <div className="grid h-14 w-14 place-items-center rounded-xl bg-slate-100"><FileWarning className="h-5 w-5 text-slate-500" /></div>}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900">{report.contentTitle || 'Reported content'}</p>
            <p className="mt-1 text-xs text-slate-600">{report.reason} · {statusLabel(report.status)}</p>
            <p className="mt-1 text-[11px] text-slate-500">{report.details || 'No additional details.'}</p>
            <p className="mt-1 text-[10px] text-slate-400">{reportDate(report.reportedAt)}</p>
            <button type="button" onClick={() => toggleReport(report.id)} className="mt-2 text-[10px] font-bold text-blue-800">{expandedReport === report.id ? 'Hide admin activity' : 'View admin activity'}</button>
            {eventsLoading === report.id && <p className="mt-1 text-[10px] text-slate-500">Loading activity…</p>}
            {lastAdminEvent && <p className="mt-1 text-[10px] text-slate-600">{eventLabel(lastAdminEvent, false)} · {reportDate(lastAdminEvent.createdAt)}</p>}
          </div>
        </article>;
      })}

      {section === 'history' && data.timeline.map(event => <article key={`${event.reportId}-${event.id}`} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <div className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-50 text-blue-800"><MessageSquareText className="h-4 w-4" /></div>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-800">{eventLabel(event, true)}</p>
          <p className="mt-1 truncate text-[11px] text-slate-500">{event.contentTitle || 'Reported content'}</p>
          <p className="mt-1 text-[10px] text-slate-400">{reportDate(event.createdAt)}</p>
        </div>
      </article>)}

      {loading && <div className="flex justify-center py-5"><LoaderCircle className="h-5 w-5 animate-spin text-blue-800" /></div>}
      {!loading && currentItems.length === 0 && !data.error && <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center"><FileWarning className="mx-auto h-8 w-8 text-slate-400" /><p className="mt-2 text-sm font-semibold text-slate-700">{section === 'received' ? 'No reports have been received.' : section === 'filed' ? 'You have not filed any reports.' : 'No report activity yet.'}</p></div>}
      {!loading && hasMore && <button type="button" onClick={() => void data.loadReportPage(section === 'history' ? 'timeline' : section)} className="w-full rounded-xl border border-slate-200 bg-white py-3 text-xs font-bold text-slate-700">Load more</button>}
      {responseReport && <ReportResponseModal report={responseReport} onClose={() => setResponseReport(null)} onSaved={() => {
        setEventsByReport(previous => { const next = { ...previous }; delete next[responseReport.id]; return next; });
        void loadEvents(responseReport.id, true);
      }} />}
    </section>
  );
}
